import { useState } from "react";

import { useGenerateWorkbenchApiKey } from "@vega-api/generated/api-gateway-workbench-v1/api-gateway-workbench-v1";
import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { type ApiGatewayWorkbenchGeneratedApiKeyResponseData } from "@vega-api/generated/types/apiGatewayWorkbenchGeneratedApiKeyResponseData";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { RevealKeyStep } from "./RevealKeyStep";
import { DEFAULT_TTL_DAYS, isValidTtl, TtlSelect } from "./TtlSelect";

const CONFIRM_TEXT = "ROTATE";

type RotateKeyDialogProps = {
  apiKey: ApiGatewayWorkbenchApiKey;
  onClose: VoidFunction;
  onRotated: VoidFunction;
};

/**
 * Rotation is the same upsert as generation (same userId + keyName): the
 * backend replaces the hash and resets expiry, and the old key stops working
 * immediately – no grace period.
 */
export const RotateKeyDialog = ({
  apiKey,
  onClose,
  onRotated,
}: RotateKeyDialogProps) => {
  const [ttlDays, setTtlDays] = useState(DEFAULT_TTL_DAYS);
  const [confirmInput, setConfirmInput] = useState("");
  const [generatedKey, setGeneratedKey] =
    useState<ApiGatewayWorkbenchGeneratedApiKeyResponseData | null>(null);
  const showToast = useToast();

  const { mutate: rotateKey, isPending } = useGenerateWorkbenchApiKey({
    mutation: {
      onSuccess: (response) => {
        setGeneratedKey(response.data ?? null);
        showToast({
          title: m.settings_reportingApiKeys_toast_generated(),
          variant: "success",
        });
        onRotated();
      },
      onError: () => {
        showToast({
          title: m.settings_reportingApiKeys_toast_error(),
          variant: "destructive",
        });
      },
    },
  });

  const isRevealing = generatedKey !== null;

  return (
    <ActionaryModal
      ModalButtonsGroupProps={
        isRevealing
          ? undefined
          : {
              mainActionText: m.settings_reportingApiKeys_rotate_confirm(),
              mainActionVariant: "destructive",
              mainAction: () => {
                if (!isValidTtl(ttlDays)) return;
                rotateKey({
                  userId: apiKey.userId,
                  keyName: apiKey.keyName,
                  params: { ttlDays },
                });
              },
              disabled: confirmInput !== CONFIRM_TEXT,
              isLoading: isPending,
              secondaryActionText: m.btn_cancel(),
              secondaryAction: onClose,
            }
      }
      dataTestId="reporting-api-keys-rotate-dialog"
      hideCloseButton={isRevealing}
      open
      setOpen={(value) => {
        // No Esc/click-outside dismissal while the plaintext is on screen.
        if (!value && isRevealing) return;

        if (!value) onClose();
      }}
      title={
        isRevealing ? (
          m.settings_reportingApiKeys_reveal_title()
        ) : (
          <div className="flex flex-col gap-1">
            <span>{m.settings_reportingApiKeys_rotate_title()}</span>
            <span className="body-base-regular text-text/secondary break-all">
              {apiKey.keyName}
            </span>
          </div>
        )
      }
    >
      {isRevealing ? (
        <RevealKeyStep generatedKey={generatedKey} onDone={onClose} />
      ) : (
        <div className="flex flex-col gap-4 text-left">
          <p className="body-base-regular text-text/secondary">
            {m.settings_reportingApiKeys_rotate_explainer()}
          </p>
          <Notice
            content={m.settings_reportingApiKeys_rotate_warning()}
            title={m.settings_reportingApiKeys_noGracePeriod()}
            type="warning"
            variant="accent"
          />
          <TtlSelect onChange={setTtlDays} value={ttlDays} />
          <InputControl
            label={m.settings_reportingApiKeys_rotate_typeToConfirm()}
            name="reporting-api-keys-rotate-confirm"
            onChange={(event) => setConfirmInput(event.target.value)}
            placeholder={CONFIRM_TEXT}
            type="text"
            value={confirmInput}
            withBorder
          />
        </div>
      )}
    </ActionaryModal>
  );
};
